import { useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';

// comps
import { TableCell, ClickAwayListener, Tooltip, tooltipClasses } from '@mui/material';
import { Upload, UploadFileStatus } from '@progress/kendo-react-upload';
import GlobalInput from '../global/input';
import GlobalDropdown from '../global/dropdown';
import GlobalCheckbox from '../global/checkbox';

// constants
import { MOMENT_DATE_FORMAT, MOMENT_DATE_DISPLAY_FORMAT } from '../../constants/date_format';
import { colors } from '../../theme';
import { getApiUrl } from '../../helpers/api-url';
import { authHeader } from '../../utils/auth-header';

// icons
import { ReactComponent as FileIcon } from '../../assets/icons/file-icon.svg';

export const CustomTableInputCell = (props: any) => {
    const {
        data,
        name,
        onChange,
        sx,
        type,
        center,
        parse,
        isDefault,
        placeholder,
        id,
        maxLength,
        inputValue = '',
        accept,
        onFocus,
        toUpperCase,
        tooltipMsg,
        truncateTextAndShowTooltipOnlyWhenLong,
    } = props;
    const { isEditMode } = data;

    let value = inputValue !== '' ? inputValue : data[name];
    const valueLength = window.innerWidth <= 1480 ? 30 : 55;

    const [changeColor, setChangeColor] = useState('');

    const handleChangeColor = () => {
        if (id) setChangeColor(data[id]);
    };

    const handleMousLeave = () => {
        setChangeColor('');
    };

    const renderTableValue = (value, type) => {
        if ((!type || !value) && type !== 'currency') {
            if (truncateTextAndShowTooltipOnlyWhenLong) {
                return (value ? value?.slice(0, valueLength) : '') + (value?.length > valueLength ? ' ...' : '');
            } else {
                return value;
            }
        }

        switch (type) {
            case 'date': {
                return moment(value).format(MOMENT_DATE_DISPLAY_FORMAT);
            }
            case 'currency': {
                return new Intl.NumberFormat('en-ca', { style: 'currency', currency: 'CAD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value ? value : 0)
            }
            default: {
                if (truncateTextAndShowTooltipOnlyWhenLong) {
                    return (value ? value?.slice(0, valueLength) : '') + (value?.length > valueLength ? ' ...' : '');
                } else {
                    return value;
                }
            }
        }
    };

    //currently this has same implementation as renderTableValue but overtime displaying (formatting) value will change vs value for the input
    const getInputValue = (value, type) => {
        if (!type || !value) return value;

        switch (type) {
            case 'date': {
                return moment(value).format(MOMENT_DATE_FORMAT);
            }
            default: {
                return value;
            }
        }
    };

    return (
        <ClickAwayListener onClickAway={handleMousLeave}>
            <Tooltip
                title={!isEditMode && !isDefault ? (truncateTextAndShowTooltipOnlyWhenLong ? (tooltipMsg?.length > valueLength ? tooltipMsg : '') : tooltipMsg) : ''}
                arrow
                placement="top"
                PopperProps={{
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, -15],
                            },
                        },
                    ],
                }}
            >
                <TableCell align={center ? 'center' : 'left'} sx={sx}>
                    {isEditMode || isDefault ? (
                        <GlobalInput
                            // sx={sx}
                            id={data[id] || null}
                            placeholder={placeholder}
                            type={type}
                            parse={parse}
                            center={center}
                            value={type !== 'file' && getInputValue(value, type)}
                            name={name}
                            onChange={(name: any, value: any) => {
                                if (toUpperCase) {
                                    onChange(name, value.toUpperCase(), data);
                                } else onChange(name, value, data);
                            }}
                            table={'true'}
                            changecolor={changeColor}
                            onFocus={() => onFocus && onFocus(data)}
                            onColorChange={handleChangeColor}
                            inputProps={{
                                ...props.inputProps,
                                min: type === 'number' && '0',
                                maxLength,
                                accept: accept,
                            }}
                        />
                    ) : (
                        renderTableValue(value, type)
                    )}
                </TableCell>
            </Tooltip>
        </ClickAwayListener>
    );
};

export const CustomTableDropdownCell = (props: any) => {
    const { data, items, onChange, width, value } = props;
    const { isEditMode } = data;

    return (
        <TableCell align="left" sx={{ width: width }}>
            {isEditMode ? <GlobalDropdown {...props} data={items} value={!value ? '' : value} table={'true'} onChange={(name: any, value: any) => onChange(name, value, data)} /> : value}
        </TableCell>
    );
};

export const CustomTableCheckboxCell = (props: any) => {
    const { data, name, onChange, disabled, width, isDefault } = props;

    const { isEditMode } = data;
    const defaultIsChecked = isDefault && data[name] === null;

    let value = data[name];
    if (defaultIsChecked) value = true;

    return (
        <TableCell align="center" sx={{ width: width ? width : 10 }}>
            <GlobalCheckbox
                {...props}
                isChecked={defaultIsChecked ? true : data[name]}
                sx={{ p: '0px' }}
                disabled={!isEditMode || disabled ? true : false}
                onChange={(name: any) => {
                    if (defaultIsChecked) onChange(name, true, data, 'checkbox');
                    else onChange(name, value, data, 'checkbox');
                }}
                name={name}
            />
        </TableCell>
    );
};

export const CustomTableDateCell = (props: any) => {
    const { data, name, onChange, width } = props;
    const { isEditMode } = data;
    let value = data[name];

    return (
        <TableCell sx={{ width: width }}>
            {isEditMode ? (
                <GlobalInput type="date" value={moment(value).format('YYYY-MM-DD')} onChange={(name, value) => onChange(name, value, data)} name={name} />
            ) : (
                moment(value).format('MMM-DD-YYYY')
            )}
        </TableCell>
    );
};

export const CustomUploadCell = (props: any) => {
    const { data, displayField, saveUrl, onAdd, onChange, width } = props;
    const { isEditMode } = data;
    const examFile = data[displayField];

    let file = [{ name: examFile, extention: '.pdf', progress: 0, status: UploadFileStatus.Initial, uid: null }];

    const onStatusChange = (event: any) => {
        const file = event.affectedFiles[0];
        if (file.status === 4) {
            const examFileID = event.response.response;
            const fileName = file.name;
            onChange(examFileID, fileName, data, 'fileUpload');
        }
    };

    return (
        <TableCell sx={{ width: width }}>
            {isEditMode ? (
                <StyledUpload
                    accept={'.pdf'}
                    batch={false}
                    multiple={false}
                    restrictions={{
                        allowedExtensions: ['.pdf'],
                    }}
                    saveUrl={getApiUrl() + saveUrl}
                    saveField="file"
                    removeField="file"
                    defaultFiles={file}
                    withCredentials={false}
                    saveMethod="Post"
                    onAdd={onAdd}
                    onStatusChange={onStatusChange}
                    saveHeaders={authHeader()}
                    selectMessageUI={() => {
                        return <span>Select file...</span>;
                    }}
                />
            ) : (
                <IconContainer>
                    <FileIcon />
                    {examFile}
                </IconContainer>
            )}
        </TableCell>
    );
};

const StyledUpload = styled(Upload)`
    && {
        font-size: 12px;
        font-family: 'Roboto';

        &.k-upload {
            border-radius: 6px;
            border-color: ${colors.border};
        }

        .k-dropzone {
            background-color: ${colors.white};
            border-radius: 6px;
            border: none;
            padding: 3px 5px;
            text-align: left;
        }

        .k-button {
            background-color: ${colors.blue.main};
            border: none;
            color: ${colors.white};
            font-size: 12px;
            max-height: 22px;
            min-width: 5em;
            padding: 5px 10px;
            margin-right: 1em;
        }

        .k-file-validation-message {
            display: none;
        }
    }
`;

const IconContainer = styled.span`
    display: flex;
    align-items: center;

    svg {
        margin-right: 5px;
    }
`;

